import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDeliveryDropdownOptions,
  getPackTypeForDeliveryLineItem,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';
import { ReactComponent as DocumentIcon } from 'src/Images/icons/planning/Document.svg';
import { ReactComponent as PackQuantityIcon } from 'src/Images/icons/planning/PackQuantity.svg';
import { ReactComponent as DeliveryQuantityIcon } from 'src/Images/icons/planning/DeliveryQuantity.svg';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';

const today = new Date().toISOString().split('T')[0];

const clampToZero = (value) => Math.max(0, Number(value) || 0);
const incrementValue = (currentValue) =>
  clampToZero((Number(currentValue) || 0) + 1);
const decrementValue = (currentValue) =>
  clampToZero((Number(currentValue) || 0) - 1);

const combineUniqueDeliveriesById = (primaryList = [], secondaryList = []) => {
  const merged = [...primaryList];
  const existingIds = new Set(primaryList.map((item) => item.delivery_id));

  secondaryList.forEach((deliveryObj) => {
    if (!existingIds.has(deliveryObj.delivery_id)) {
      existingIds.add(deliveryObj.delivery_id);
      merged.push(deliveryObj);
    }
  });

  return merged;
};

function DeliverySummaryCard({ Icon, label, value, uom, themeColor }) {
  const displayValue =
    typeof value === 'number' && uom !== 'Total' ? value.toFixed(2) : value;

  return (
    <div className="planning-dashboard-card div-flex-row div-space-between">
      <div className="div-flex-column rg-5 div-align-start">
        <span className="text-md text-semi-bold text-nowrap">{label}</span>
        <div className="div-flex-row cg-5 div-align-baseline">
          <span className="text-xxl text-bold">{displayValue}</span>
          <span className="text-md text-grey">{uom}</span>
        </div>
      </div>
      <div className="div-flex-column div-align-end">
        <span
          className={`delivery-summary-icon theme-text-color-${themeColor}`}
        >
          <Icon className="icon-size-xxxl" />
        </span>
      </div>
    </div>
  );
}

function CreateLoadForVehicleTrackingPlanning({
  formData,
  selectedDeliveryOrder,
  loadLineItems,
  setLoadLineItems,
  onBack,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer.userData.organisations?.id;

  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const { packTypeForDeliveryLineItem } = VehicleTrackingOutwardReducer;

  const [activeTab, setActiveTab] = useState('loadLineItems');
  const [showFilter, setShowFilter] = useState(false);
  const [searchedDeliveryList, setSearchedDeliveryList] = useState(null);
  const [activePackTypeSummaryKey, setActivePackTypeSummaryKey] =
    useState(null);
  const [packTypeOptionsByLineItemId, setPackTypeOptionsByLineItemId] =
    useState({});
  const [filterFormData, setFilterFormData] = useState({
    deliveryNumber: '',
    deliveryStartDate: today,
    deliveryEndDate: today,
  });

  const attachedDeliveryList = useMemo(
    () =>
      combineUniqueDeliveriesById(
        selectedDeliveryOrder || [],
        searchedDeliveryList?.delivery || [],
      ),
    [selectedDeliveryOrder, searchedDeliveryList],
  );

  useEffect(() => {
    if (!packTypeForDeliveryLineItem?.deliveryLineItemId) return;

    const transformedList =
      packTypeForDeliveryLineItem.packTypeList?.map((item) => ({
        label: item?.name,
        value: item?.id,
      })) || [];

    setPackTypeOptionsByLineItemId((prev) => ({
      ...prev,
      [packTypeForDeliveryLineItem.deliveryLineItemId]: transformedList,
    }));
  }, [packTypeForDeliveryLineItem]);

  const getPackTypeOptionsForLineItem = (deliveryLineItemId) =>
    packTypeOptionsByLineItemId[deliveryLineItemId] || [];

  const handlePackTypeMenuOpen = (deliveryLineItemId) => {
    if (!tenantOrganisationId || !deliveryLineItemId) return;
    if (packTypeOptionsByLineItemId[deliveryLineItemId]) return;

    dispatch(
      getPackTypeForDeliveryLineItem(tenantOrganisationId, deliveryLineItemId),
    );
  };

  const deliverySelectionSummary = useMemo(() => {
    const deliveries = loadLineItems || [];
    const packTypeSummaryMap = {};

    deliveries.forEach((item) => {
      const packTypeValue =
        typeof item.pack_type === 'object'
          ? item.pack_type?.label || item.pack_type?.name
          : item.pack_type;
      const packTypeKey = packTypeValue || 'unassigned';
      const packTypeName = packTypeValue || 'Unassigned';

      if (!packTypeSummaryMap[packTypeKey]) {
        packTypeSummaryMap[packTypeKey] = {
          summaryKey: `pack-type-${packTypeKey}`,
          packTypeName,
          totalDocuments: 0,
          totalPackQuantity: 0,
          totalDeliveryQuantity: 0,
          totalGrossWeight: 0,
          quantityUOM: item?.line_items?.[0]?.unit_of_measurement?.name,
        };
      }

      packTypeSummaryMap[packTypeKey].totalDocuments += 1;
      packTypeSummaryMap[packTypeKey].totalPackQuantity += Number(
        item.pack_quantity || 0,
      );
      packTypeSummaryMap[packTypeKey].totalDeliveryQuantity += Number(
        item.total_quantity || 0,
      );
      packTypeSummaryMap[packTypeKey].totalGrossWeight += Number(
        item.weight || 0,
      );
    });

    return Object.values(packTypeSummaryMap);
  }, [loadLineItems]);

  const activePackTypeSummary =
    deliverySelectionSummary.find(
      (summary) => summary.summaryKey === activePackTypeSummaryKey,
    ) || deliverySelectionSummary[0];

  const activeSummaryCards = [
    {
      label: 'Total Documents',
      key: 'total-documents',
      value: activePackTypeSummary?.totalDocuments || 0,
      uom: 'Nos',
      Icon: DocumentIcon,
    },
    {
      label: 'Total Pack Quantity',
      key: 'total-pack-quantity',
      value: activePackTypeSummary?.totalPackQuantity || 0,
      uom: 'Nos',
      Icon: PackQuantityIcon,
    },
    {
      label: 'Total Delivery Quantity',
      key: 'total-delivery-quantity',
      value: activePackTypeSummary?.totalDeliveryQuantity || 0,
      uom: activePackTypeSummary?.quantityUOM || '',
      Icon: DeliveryQuantityIcon,
    },
    {
      label: 'Gross Weight',
      key: 'gross-weight',
      value: activePackTypeSummary?.totalGrossWeight || 0,
      uom: activePackTypeSummary?.quantityUOM || '',
      Icon: DeliveryQuantityIcon,
    },
  ];

  const handleDateChange = (field, value) => {
    setFilterFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearchSubmit = () => {
    if (!tenantOrganisationId) return;

    const deliverySearchTerm =
      filterFormData.deliveryNumber?.trim() || undefined;

    dispatch(
      getDeliveryDropdownOptions(
        tenantOrganisationId,
        formData?.vehicleNumber || null,
        1,
        null,
        filterFormData.deliveryStartDate,
        filterFormData.deliveryEndDate,
        null,
        deliverySearchTerm,
        false,
      ),
    )
      .then((response) => {
        const responseBody = response?.data || response || {};
        setSearchedDeliveryList({
          delivery: responseBody.delivery || [],
          totalPages: responseBody.totalPages || 1,
        });
      })
      .catch(() => {
        setSearchedDeliveryList(null);
      });
  };

  const isDeliverySelected = (lineItemObj) =>
    (loadLineItems || []).some(
      (selectedLineItemObj) =>
        selectedLineItemObj?.delivery_id === lineItemObj?.delivery_id,
    );

  const handleToggleDeliverySelection = (lineItemObj, checked) => {
    if (!lineItemObj) return;

    setLoadLineItems((prev) => {
      const alreadySelected = prev.some(
        (selectedLineItemObj) =>
          selectedLineItemObj?.delivery_id === lineItemObj?.delivery_id,
      );

      if (checked) {
        return alreadySelected ? prev : [...prev, lineItemObj];
      }

      return prev.filter(
        (selectedLineItemObj) =>
          selectedLineItemObj?.delivery_id !== lineItemObj?.delivery_id,
      );
    });
  };

  const isAllDeliveriesSelectedInList = (lineItemObjList) =>
    (lineItemObjList || []).length > 0 &&
    lineItemObjList.every((lineItemObj) => isDeliverySelected(lineItemObj));

  const handleSelectAllInList = (lineItemObjList, checked) => {
    if (checked) {
      setLoadLineItems((prev) => {
        const newSelections = (lineItemObjList || []).filter(
          (lineItemObj) =>
            !prev.some((item) => item.delivery_id === lineItemObj.delivery_id),
        );
        return [...prev, ...newSelections];
      });
    } else {
      setLoadLineItems((prev) =>
        prev.filter(
          (item) =>
            !(lineItemObjList || []).some(
              (lineItemObj) => lineItemObj.delivery_id === item.delivery_id,
            ),
        ),
      );
    }
  };

  const handleRemoveLoadLineItem = (deliveryId) => {
    if (!deliveryId) return;

    setLoadLineItems((prev) =>
      prev.filter((item) => item.delivery_id !== deliveryId),
    );
  };

  // ---- Editable field handlers (Load Line Items tab only) ----

  const handleQuantityChange = (deliveryId, lineItemId, value) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) => {
        if (deliveryObj?.delivery_id !== deliveryId) return deliveryObj;

        return {
          ...deliveryObj,
          line_items: (deliveryObj.line_items || []).map((item) =>
            item?.delivery_line_item_id === lineItemId
              ? { ...item, quantity: clampToZero(value) }
              : item,
          ),
        };
      }),
    );
  };

  const handleQuantityIncrement = (deliveryId, lineItemId) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) => {
        if (deliveryObj?.delivery_id !== deliveryId) return deliveryObj;

        return {
          ...deliveryObj,
          line_items: (deliveryObj.line_items || []).map((item) =>
            item?.delivery_line_item_id === lineItemId
              ? { ...item, quantity: incrementValue(item?.quantity) }
              : item,
          ),
        };
      }),
    );
  };

  const handleQuantityDecrement = (deliveryId, lineItemId) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) => {
        if (deliveryObj?.delivery_id !== deliveryId) return deliveryObj;

        return {
          ...deliveryObj,
          line_items: (deliveryObj.line_items || []).map((item) =>
            item?.delivery_line_item_id === lineItemId
              ? { ...item, quantity: decrementValue(item?.quantity) }
              : item,
          ),
        };
      }),
    );
  };

  const handlePackTypeChange = (deliveryId, selectedOption) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) =>
        deliveryObj?.delivery_id === deliveryId
          ? { ...deliveryObj, pack_type: selectedOption }
          : deliveryObj,
      ),
    );
  };

  const handlePackQuantityChange = (deliveryId, value) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) =>
        deliveryObj?.delivery_id === deliveryId
          ? { ...deliveryObj, pack_quantity: clampToZero(value) }
          : deliveryObj,
      ),
    );
  };

  const handlePackQuantityIncrement = (deliveryId) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) => {
        if (deliveryObj?.delivery_id !== deliveryId) return deliveryObj;
        return {
          ...deliveryObj,
          pack_quantity: incrementValue(deliveryObj.pack_quantity),
        };
      }),
    );
  };

  const handlePackQuantityDecrement = (deliveryId) => {
    setLoadLineItems((prev) =>
      prev.map((deliveryObj) => {
        if (deliveryObj?.delivery_id !== deliveryId) return deliveryObj;
        return {
          ...deliveryObj,
          pack_quantity: decrementValue(deliveryObj.pack_quantity),
        };
      }),
    );
  };

  const renderDeliverySummary = () => (
    <div
      className="div-flex-column rg-10 vertical-padding-10 sticky-top bg-white"
      style={{ position: 'sticky', top: 0, zIndex: 2 }}
    >
      <div className="div-flex-row cg-5 border-bottom padding-bottom-8">
        {deliverySelectionSummary.length > 0 ? (
          deliverySelectionSummary.map((packTypeSummary) => {
            const isActive =
              packTypeSummary.summaryKey === activePackTypeSummary?.summaryKey;

            return (
              <button
                key={packTypeSummary.summaryKey}
                type="button"
                className={`div-flex-row div-align-center text-sm ${
                  isActive
                    ? `theme-pill-active-${themeColor}`
                    : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
                }`}
                onClick={() =>
                  setActivePackTypeSummaryKey(packTypeSummary.summaryKey)
                }
              >
                <span>{packTypeSummary.packTypeName}</span>
              </button>
            );
          })
        ) : (
          <button
            type="button"
            className="border border-radius-4 vertical-padding-5 horizontal-padding-10 text-sm"
          >
            <span>Unassigned</span>
          </button>
        )}
      </div>
      <div className="div-flex-row cg-5 delivery-selection-summary-row">
        {activeSummaryCards.map((summaryCard) => (
          <div
            key={summaryCard.key}
            className="delivery-selection-summary-item"
          >
            <DeliverySummaryCard
              Icon={summaryCard.Icon}
              label={summaryCard.label}
              value={summaryCard.value}
              uom={summaryCard.uom}
              themeColor={themeColor}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderAttachedDeliveryRows = (lineItemObjList, emptyMessage) => {
    if (!lineItemObjList?.length) {
      return (
        <tr>
          <td colSpan="12" className="text-center text-sm text-grey">
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return lineItemObjList.flatMap((lineItemObj) => {
      const deliveryLineItems = lineItemObj.line_items || [];
      const tableRows = deliveryLineItems.length ? deliveryLineItems : [{}];
      const checked = isDeliverySelected(lineItemObj);

      return tableRows.map((item, idx) => (
        <tr
          key={
            item?.delivery_line_item_id || `${lineItemObj?.delivery_id}-${idx}`
          }
        >
          <td>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) =>
                handleToggleDeliverySelection(lineItemObj, e.target.checked)
              }
            />
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {lineItemObj?.sap_code || lineItemObj?.delivery_code || '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {lineItemObj?.delivery_date ?? '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {lineItemObj?.weight ?? '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {item?.product?.product_sap_code ?? '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {item?.plant?.name ?? '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {typeof lineItemObj?.pack_type === 'object'
                ? lineItemObj?.pack_type?.label ||
                  lineItemObj?.pack_type?.name ||
                  '-'
                : lineItemObj?.pack_type || '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {lineItemObj?.pack_quantity ?? '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">{item?.quantity ?? '-'}</span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {lineItemObj?.grade ?? '-'}
            </span>
          </td>
          <td>
            <span className="text-sm text-nowrap">{item?.batch ?? '-'}</span>
          </td>
          <td>
            <span className="text-sm text-nowrap">
              {lineItemObj?.sales_order_number ?? '-'}
            </span>
          </td>
        </tr>
      ));
    });
  };

  const renderLoadLineItemRows = (lineItemObjList, emptyMessage) => {
    if (!lineItemObjList?.length) {
      return (
        <tr>
          <td colSpan="12" className="text-center text-sm text-grey">
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return lineItemObjList.flatMap((lineItemObj) => {
      const deliveryLineItems = lineItemObj.line_items || [];
      const tableRows = deliveryLineItems.length ? deliveryLineItems : [{}];
      const rowSpan = tableRows.length;
      const deliveryId = lineItemObj?.delivery_id;
      const firstLineItemId = tableRows[0]?.delivery_line_item_id;

      return tableRows.map((item, idx) => {
        const lineItemId = item?.delivery_line_item_id;

        return (
          <tr key={lineItemId || `${deliveryId}-${idx}`}>
            <td>
              <span className="text-sm text-nowrap">
                {lineItemObj?.sap_code || lineItemObj?.delivery_code || '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {lineItemObj?.delivery_date ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {lineItemObj?.weight ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {item?.product?.product_sap_code ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {item?.plant?.name ?? '-'}
              </span>
            </td>
            <td>
              <div className="input-wrapper">
                <CustomMultiSelect
                  isMulti={false}
                  name="pack_type"
                  className="multi-select-input-section"
                  classNamePrefix="react-select"
                  placeholder="Select Pack Type"
                  options={getPackTypeOptionsForLineItem(firstLineItemId)}
                  value={lineItemObj?.pack_type || null}
                  onChange={(selectedOption) =>
                    handlePackTypeChange(deliveryId, selectedOption)
                  }
                  onMenuOpen={() => handlePackTypeMenuOpen(firstLineItemId)}
                  isClearable
                />
              </div>
            </td>
            <td>
              <div className="input-wrapper">
                <div className="form-input-section div-align-center div-flex-row div-space-between">
                  <div
                    role="button"
                    onClick={() => handlePackQuantityDecrement(deliveryId)}
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">-</span>
                  </div>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={lineItemObj?.pack_quantity ?? 0}
                    onChange={(e) =>
                      handlePackQuantityChange(
                        deliveryId,
                        Number(e.target.value) || 0,
                      )
                    }
                    className="input-field-none text-sm text-semi-bold"
                  />
                  <div
                    role="button"
                    onClick={() => handlePackQuantityIncrement(deliveryId)}
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">+</span>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <div className="input-wrapper">
                <div className="form-input-section div-align-center div-flex-row div-space-between">
                  <div
                    role="button"
                    onClick={() =>
                      handleQuantityDecrement(deliveryId, lineItemId)
                    }
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">-</span>
                  </div>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={item?.quantity ?? 0}
                    onChange={(e) =>
                      handleQuantityChange(
                        deliveryId,
                        lineItemId,
                        Number(e.target.value) || 0,
                      )
                    }
                    className="input-field-none text-sm text-semi-bold"
                  />
                  <div
                    role="button"
                    onClick={() =>
                      handleQuantityIncrement(deliveryId, lineItemId)
                    }
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">+</span>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {lineItemObj?.grade ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">{item?.batch ?? '-'}</span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {lineItemObj?.sales_order_number ?? '-'}
              </span>
            </td>
            <td rowSpan={rowSpan}>
              <div
                className="div-flex-column div-align-center"
                role="button"
                tabIndex={0}
                aria-label="Remove line item"
                onClick={() => handleRemoveLoadLineItem(deliveryId)}
              >
                <Delete className="icon-size-sm" />
              </div>
            </td>
          </tr>
        );
      });
    });
  };

  return (
    <div className="div-flex-column rg-10 h-100 padding-top-10">
      <div className="div-flex-row div-space-between div-align-center">
        <div className="div-flex-row cg-20 div-align-center horizontal-padding-16">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-bold">Create Load</span>
          </div>
        </div>
      </div>

      <div className="div-flex-row cg-10 padding-bottom-10 text-sm horizontal-padding-16">
        <button
          type="button"
          className={
            activeTab === 'loadLineItems'
              ? `theme-pill-active-${themeColor}`
              : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
          }
          onClick={() => setActiveTab('loadLineItems')}
        >
          Load Line Items
        </button>
        <button
          type="button"
          className={
            activeTab === 'attachedDeliveries'
              ? `theme-pill-active-${themeColor}`
              : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
          }
          onClick={() => setActiveTab('attachedDeliveries')}
        >
          Attached Deliveries
        </button>
      </div>

      <div className="div-flex-column container-card-shadow border-radius-4 min-height-20vh">
        <div className="container-padding-5 padding">
          <div className="container-padding-10">
            {activeTab === 'attachedDeliveries' && (
              <>
                {showFilter && (
                  <Row className="w-100 margin-bottom-15">
                    <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                      <div className="div-flex-column">
                        <span className="text-sm text-bold">
                          Delivery Number
                        </span>
                        <div className="input-wrapper">
                          <input
                            className="form-input-section margin-top-5"
                            type="text"
                            placeholder="Search Delivery Number"
                            value={filterFormData.deliveryNumber}
                            onChange={(e) =>
                              setFilterFormData((prev) => ({
                                ...prev,
                                deliveryNumber: e.target.value,
                              }))
                            }
                          />
                          <span className="form-input-section-icon">
                            <Search className="icon-size-sm" color="#9F9F9F" />
                          </span>
                        </div>
                      </div>
                    </Col>

                    <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                      <div className="div-flex-column">
                        <span className="text-sm text-bold">
                          Delivery Start Date
                        </span>
                        <input
                          type="date"
                          className="form-input-section margin-top-5"
                          value={filterFormData.deliveryStartDate}
                          onChange={(e) =>
                            handleDateChange(
                              'deliveryStartDate',
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </Col>

                    <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                      <div className="div-flex-column">
                        <span className="text-sm text-bold">
                          Delivery End Date
                        </span>
                        <input
                          type="date"
                          className="form-input-section margin-top-5"
                          value={filterFormData.deliveryEndDate}
                          onChange={(e) =>
                            handleDateChange('deliveryEndDate', e.target.value)
                          }
                        />
                      </div>
                    </Col>

                    <Col
                      xxl={3}
                      xl={3}
                      lg={3}
                      md={12}
                      sm={12}
                      className="d-flex justify-content-end align-items-end"
                    >
                      <button
                        type="button"
                        className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                        onClick={handleSearchSubmit}
                      >
                        Search
                      </button>
                    </Col>
                  </Row>
                )}

                <div className="purchase-order-line-item-card border-radius-4 margin-top-15">
                  <div className="div-space-between div-align-center px-2 py-2 border-bottom">
                    <span className="text-md text-bold delivery-header">
                      Attached Deliveries
                    </span>
                    <button
                      className="icon-btn-none"
                      onClick={() => setShowFilter((prev) => !prev)}
                      aria-label="Funnel"
                      type="button"
                    >
                      <Funnel
                        className="icon-size-lg"
                        color={showFilter ? '#000000' : '#9F9F9F'}
                      />
                    </button>
                  </div>
                  <div
                    className={`load-item-table-wrapper po-table-scroll-container theme-scrollable-container-${themeColor}`}
                  >
                    <table className="load-item-table">
                      <thead className="outward-delivery-table-head">
                        <tr>
                          <th className="table-head-40">
                            <input
                              type="checkbox"
                              checked={isAllDeliveriesSelectedInList(
                                attachedDeliveryList,
                              )}
                              onChange={(e) =>
                                handleSelectAllInList(
                                  attachedDeliveryList,
                                  e.target.checked,
                                )
                              }
                              aria-label="Select all deliveries"
                            />
                          </th>
                          <th className="table-head-120">
                            <span className="text-sm text-grey text-semi-bold">
                              Delivery Number
                            </span>
                          </th>
                          <th className="table-head-100">
                            <span className="text-sm text-grey text-semi-bold">
                              Delivery Date
                            </span>
                          </th>
                          <th className="table-head-100">
                            <span className="text-sm text-grey text-semi-bold">
                              Gross Weight
                            </span>
                          </th>
                          <th className="table-head-150">
                            <span className="text-sm text-grey text-semi-bold">
                              Product SAP Code
                            </span>
                          </th>
                          <th className="table-head-60">
                            <span className="text-sm text-grey text-semi-bold">
                              Plant
                            </span>
                          </th>
                          <th className="table-head-150">
                            <span className="text-sm text-grey text-semi-bold">
                              Pack Type
                            </span>
                          </th>
                          <th className="table-head-120">
                            <span className="text-sm text-grey text-semi-bold">
                              Pack Quantity
                            </span>
                          </th>
                          <th className="table-head-120">
                            <span className="text-sm text-grey text-semi-bold">
                              Net Weight
                            </span>
                          </th>
                          <th className="table-head-100">
                            <span className="text-sm text-grey text-semi-bold">
                              Grade
                            </span>
                          </th>
                          <th className="table-head-80">
                            <span className="text-sm text-grey text-semi-bold">
                              Batch
                            </span>
                          </th>
                          <th className="table-head-150">
                            <span className="text-sm text-grey text-semi-bold">
                              Sales Order Number
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderAttachedDeliveryRows(
                          attachedDeliveryList,
                          'No Delivery Attached Yet.',
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'loadLineItems' && (
              <>
                {renderDeliverySummary()}

                <div className="purchase-order-line-item-card border-radius-4 margin-top-15">
                  <div className="div-space-between div-align-center px-2 py-2 border-bottom">
                    <span className="text-md text-bold delivery-header">
                      Load Line Items
                    </span>
                  </div>
                  <div
                    className={`load-item-table-wrapper po-table-scroll-container theme-scrollable-container-${themeColor}`}
                  >
                    <table className="load-item-table">
                      <thead className="outward-delivery-table-head">
                        <tr>
                          <th className="table-head-120">
                            <span className="text-sm text-grey text-semi-bold">
                              Delivery Number
                            </span>
                          </th>
                          <th className="table-head-100">
                            <span className="text-sm text-grey text-semi-bold">
                              Delivery Date
                            </span>
                          </th>
                          <th className="table-head-100">
                            <span className="text-sm text-grey text-semi-bold">
                              Gross Weight
                            </span>
                          </th>
                          <th className="table-head-150">
                            <span className="text-sm text-grey text-semi-bold">
                              Product SAP Code
                            </span>
                          </th>
                          <th className="table-head-60">
                            <span className="text-sm text-grey text-semi-bold">
                              Plant
                            </span>
                          </th>
                          <th className="table-head-150">
                            <span className="text-sm text-grey text-semi-bold">
                              Pack Type
                            </span>
                          </th>
                          <th className="table-head-120">
                            <span className="text-sm text-grey text-semi-bold">
                              Pack Quantity
                            </span>
                          </th>
                          <th className="table-head-120">
                            <span className="text-sm text-grey text-semi-bold">
                              Net Weight
                            </span>
                          </th>
                          <th className="table-head-100">
                            <span className="text-sm text-grey text-semi-bold">
                              Grade
                            </span>
                          </th>
                          <th className="table-head-80">
                            <span className="text-sm text-grey text-semi-bold">
                              Batch
                            </span>
                          </th>
                          <th className="table-head-150">
                            <span className="text-sm text-grey text-semi-bold">
                              Sales Order Number
                            </span>
                          </th>
                          <th className="table-head-60">
                            <span className="text-sm text-grey text-semi-bold">
                              Action
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderLoadLineItemRows(
                          loadLineItems,
                          'No Delivery Added Yet. Add deliveries from the Attached Deliveries tab.',
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateLoadForVehicleTrackingPlanning;
